import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import { toast } from 'react-toastify';

function Customers() {

document.title = 'Customers';

  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nickname: '', // Added nickname field
    quickbooks_id: '',
    email: '',
    phone: '',
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_zip: '',
    billing_contact_name: '',
    billing_contact_phone: '',
    billing_contact_email: '',
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    shipping_contact_name: '',
    shipping_contact_phone: '',
    shipping_contact_email: '',
    tax_id: '',
    payment_terms: '',
    notes: '',
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('customers')
          .select('*, nickname') // Ensure nickname is fetched
          .order('name');
        if (error) throw error;
        setCustomers(data || []);
      } catch (err) {
        console.error('Error fetching customers:', err);
        toast.error(`Failed to fetch customers: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [user]);

  const openModal = (mode, customer = null) => {
    setModalMode(mode);
    if (mode === 'edit' && customer) {
      setSelectedCustomer(customer);
      setFormData({
        name: customer.name || '',
        nickname: customer.nickname || '', // Populate nickname
        quickbooks_id: customer.quickbooks_id || '',
        email: customer.email || '',
        phone: customer.phone || '',
        billing_address: customer.billing_address || '',
        billing_city: customer.billing_city || '',
        billing_state: customer.billing_state || '',
        billing_zip: customer.billing_zip || '',
        billing_contact_name: customer.billing_contact_name || '',
        billing_contact_phone: customer.billing_contact_phone || '',
        billing_contact_email: customer.billing_contact_email || '',
        shipping_address: customer.shipping_address || '',
        shipping_city: customer.shipping_city || '',
        shipping_state: customer.shipping_state || '',
        shipping_zip: customer.shipping_zip || '',
        shipping_contact_name: customer.shipping_contact_name || '',
        shipping_contact_phone: customer.shipping_contact_phone || '',
        shipping_contact_email: customer.shipping_contact_email || '',
        tax_id: customer.tax_id || '',
        payment_terms: customer.payment_terms || '',
        notes: customer.notes || '',
      });
    } else {
      setSelectedCustomer(null);
      setFormData({
        name: '',
        nickname: '', // Reset nickname
        quickbooks_id: '',
        email: '',
        phone: '',
        billing_address: '',
        billing_city: '',
        billing_state: '',
        billing_zip: '',
        billing_contact_name: '',
        billing_contact_phone: '',
        billing_contact_email: '',
        shipping_address: '',
        shipping_city: '',
        shipping_state: '',
        shipping_zip: '',
        shipping_contact_name: '',
        shipping_contact_phone: '',
        shipping_contact_email: '',
        tax_id: '',
        payment_terms: '',
        notes: '',
      });
    }
    setError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCustomer(null);
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveCustomer = async () => {
    if (!formData.name) {
      setError('Customer name is required.');
      return;
    }

    try {
      if (modalMode === 'create') {
        const { error } = await supabase
          .from('customers')
          .insert(formData);
        if (error) throw error;
        toast.success('Customer created successfully!');
      } else {
        const { error } = await supabase
          .from('customers')
          .update(formData)
          .eq('id', selectedCustomer.id);
        if (error) throw error;
        toast.success('Customer updated successfully!');
      }

      const { data, error: fetchError } = await supabase
        .from('customers')
        .select('*, nickname') // Ensure nickname is fetched
        .order('name');
      if (fetchError) throw fetchError;
      setCustomers(data || []);

      closeModal();
    } catch (err) {
      console.error(`Error ${modalMode === 'create' ? 'creating' : 'updating'} customer:`, err);
      setError(`Failed to ${modalMode === 'create' ? 'create' : 'update'} customer: ${err.message}`);
      toast.error(`Failed to ${modalMode === 'create' ? 'create' : 'update'} customer: ${err.message}`);
    }
  };

  if (loading) return <div className="text-center text-gray-500 py-10">Loading...</div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
          <button
            onClick={() => openModal('create')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200"
          >
            Create Customer
          </button>
        </div>

        {/* Customers Table */}
        <div className="bg-white rounded-lg border-2 overflow-x-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nickname</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shipping Address</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {customers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50 transition duration-150">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{customer.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{customer.nickname || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{customer.email || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{customer.phone || 'N/A'}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 truncate max-w-xs" title={`${customer.shipping_address || ''}, ${customer.shipping_city || ''}, ${customer.shipping_state || ''} ${customer.shipping_zip || ''}`}>
                    {customer.shipping_address || 'N/A'}, {customer.shipping_city || 'N/A'}, {customer.shipping_state || 'N/A'} {customer.shipping_zip || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <button
                      onClick={() => openModal('edit', customer)}
                      className="text-indigo-600 hover:text-indigo-800 transition duration-150"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Create/Edit Customer Modal */}
        <Transition appear show={isModalOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={closeModal}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-lg bg-white p-6 border-2 transition-all">
                    <Dialog.Title as="h2" className="text-xl font-semibold mb-4 text-gray-900">
                      {modalMode === 'create' ? 'Create Customer' : 'Edit Customer'}
                    </Dialog.Title>

                    {error && <p className="mb-4 text-red-500">{error}</p>}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Nickname</label>
                        <input
                          type="text"
                          name="nickname"
                          value={formData.nickname}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="e.g., JBT Kingston"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">QuickBooks ID</label>
                        <input
                          type="text"
                          name="quickbooks_id"
                          value={formData.quickbooks_id}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Phone</label>
                        <input
                          type="text"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    <h3 className="text-lg font-medium text-gray-900 mb-2">Billing Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Billing Address</label>
                        <input
                          type="text"
                          name="billing_address"
                          value={formData.billing_address}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Billing City</label>
                        <input
                          type="text"
                          name="billing_city"
                          value={formData.billing_city}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Billing State</label>
                        <input
                          type="text"
                          name="billing_state"
                          value={formData.billing_state}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Billing ZIP</label>
                        <input
                          type="text"
                          name="billing_zip"
                          value={formData.billing_zip}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Billing Contact Name</label>
                        <input
                          type="text"
                          name="billing_contact_name"
                          value={formData.billing_contact_name}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Billing Contact Phone</label>
                        <input
                          type="text"
                          name="billing_contact_phone"
                          value={formData.billing_contact_phone}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Billing Contact Email</label>
                        <input
                          type="email"
                          name="billing_contact_email"
                          value={formData.billing_contact_email}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    <h3 className="text-lg font-medium text-gray-900 mb-2">Shipping Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Shipping Address</label>
                        <input
                          type="text"
                          name="shipping_address"
                          value={formData.shipping_address}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Shipping City</label>
                        <input
                          type="text"
                          name="shipping_city"
                          value={formData.shipping_city}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Shipping State</label>
                        <input
                          type="text"
                          name="shipping_state"
                          value={formData.shipping_state}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Shipping ZIP</label>
                        <input
                          type="text"
                          name="shipping_zip"
                          value={formData.shipping_zip}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Shipping Contact Name</label>
                        <input
                          type="text"
                          name="shipping_contact_name"
                          value={formData.shipping_contact_name}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Shipping Contact Phone</label>
                        <input
                          type="text"
                          name="shipping_contact_phone"
                          value={formData.shipping_contact_phone}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Shipping Contact Email</label>
                        <input
                          type="email"
                          name="shipping_contact_email"
                          value={formData.shipping_contact_email}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>

                    <h3 className="text-lg font-medium text-gray-900 mb-2">Additional Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Tax ID</label>
                        <input
                          type="text"
                          name="tax_id"
                          value={formData.tax_id}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Payment Terms</label>
                        <input
                          type="text"
                          name="payment_terms"
                          value={formData.payment_terms}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          placeholder="e.g., Net 30"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">Notes</label>
                        <textarea
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          className="mt-1 block w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          rows="3"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end space-x-4">
                      <button
                        onClick={closeModal}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveCustomer}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition duration-200"
                      >
                        {modalMode === 'create' ? 'Create Customer' : 'Update Customer'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </div>
  );
}

export default Customers;